import React, { useState, useEffect } from "react";
import '../../components/button/button.scss'
import './work.scss';
import { Link } from 'react-router-dom';
import AnimatedPage from "../../components/animated-page";
import AnimatedFeature from "../../components/animated-feature";
import RichArtifacts from "../../assets/images/copilot-rich-artifacts/copilot-rich-artifacts-cover.png"
import LoopCodeBlocks from "../../assets/images/loop-code-blocks/loop-code-blocks-cover.png"
import LoopPaywall from "../../assets/images/loop-paywall/loop-paywall-cover.png"
import MorganStanley from "../../assets/images/morgan-stanley/morgan-stanley-cover.png"
import Samana from "../../assets/images/samana/samana-cover.png"
import Blender from "../../assets/images/blender/blender-cover.png"
import GoStudioFX from "../../assets/images/gsfx/gsfx-cover.png"
import GoXLR from "../../assets/images/goxlr/goxlr-cover.png"
import Teamwrk from "../../assets/images/teamwrk/teamwrk-cover.png"
import Headshot from "../../assets/images/headshot-small.jpg";
import ArrowDown from "../../assets/icons/arrow-down.svg"
import ArrowRight from "../../assets/icons/arrow-right.svg"

const Work = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initialize
    handleResize();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Calculate the container size and image scale with parallax effect
  const calculateSizeAndScale = () => {
    if (isMobile) return { size: 130, scale: 1.4 }; // Fixed values for mobile
    
    const minSize = 80; // Much smaller final size (80px)
    const maxSize = 180; // Starting size
    const minScale = 1.8; // Slightly less zoom when smaller (reduced from 2.0)
    const maxScale = 1.2; // Lower zoom when larger
    const scrollThreshold = 150; // Smooth transition distance
    
    // No scroll - return initial values
    if (scrollY === 0) return { size: maxSize, scale: maxScale };
    
    // Full scroll - return final values
    if (scrollY >= scrollThreshold) return { size: minSize, scale: minScale };
    
    // Smooth easeOutCubic transition for both size and scale
    const progress = scrollY / scrollThreshold;
    const easedProgress = 1 - Math.pow(1 - progress, 3);
    
    const currentSize = maxSize - (maxSize - minSize) * easedProgress;
    const currentScale = maxScale + (minScale - maxScale) * easedProgress;
    
    return { size: currentSize, scale: currentScale };
  };

  const { size: imageSize, scale: imageScale } = isMobile ? 
    { size: 104, scale: 1.6 } : // Fixed 104px size on mobile with more zoom (1.6x), reduced spacing
    calculateSizeAndScale();
  
  // Calculate actual image dimensions with zoom effect built-in (no transform scaling)
  const actualImageWidth = imageSize * imageScale;
  const actualImageHeight = imageSize * imageScale;
  
  // Calculate clip-path circle positioning and image offset for left alignment
  const circleRadius = Math.floor(imageSize / 2); // Mask radius based on container size
  const circleCenterX = actualImageWidth / 2; // Center of the actual image
  const circleCenterY = actualImageHeight / 2; // Center of the actual image
  
  // Position image so the visible circle aligns to the left
  const imageLeftOffset = circleRadius - circleCenterX; // Shift image left so circle edge aligns
  
  // Different spacing for mobile vs desktop
  const spacing = isMobile ? 40 : 80; // 40px spacing on mobile, 80px on desktop
  const containerHeight = imageSize + spacing; // Visible circle size + spacing
  
  return (
    <AnimatedPage>
      <div className="work">
        <div className="intro">
          <AnimatedFeature delay={0}>
            <div style={{ height: `${containerHeight}px`, position: 'relative' }}>
              <img 
                className="image-mask" 
                src={Headshot} 
                alt="Headshot of Ravin Perera" 
                style={{ 
                  width: `${actualImageWidth}px`,
                  height: `${actualImageHeight}px`,
                  clipPath: `circle(${circleRadius}px at ${circleCenterX}px ${circleCenterY}px)`,
                  position: 'absolute',
                  top: 0,
                  left: `${imageLeftOffset}px`,
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            </div>
          </AnimatedFeature>
          <AnimatedFeature delay={0.5}>
            <div className="text">
              <h2>I'm a senior product designer at Microsoft, crafting innovative ways for humans to create and collaborate with AI.</h2>
              <div className="caption">
                <h5>Based in London, UK</h5>
                <Link className="link" to="/about" style={{color: "var(--foreground4)"}}>More about me<img src={ArrowRight} alt="Arrow right" style={{ width: "24px", marginLeft: "8px"}} /></Link>
              </div>
            </div>
          </AnimatedFeature>
          <AnimatedFeature delay={1}>
            <div className="projects-title">
              <h5>Recent work</h5><img src={ArrowDown} alt="Arrow down" style={{ width: "24px", marginLeft: "8px"}} />
            </div>
          </AnimatedFeature>
        </div>
        <div className="project-list">

        <AnimatedFeature delay={0}>
          <div id="copilot rich artifacts" className="project" onClick={()=>window.open("/copilot-rich-artifacts","_self")}>
            <div className="item">
              <div>
                <h3 style={{marginBottom: "8px"}}>Rich artifacts in Copilot Pages</h3>
                <h5>Sep 2024 - Feb 2025</h5>
              </div>
              <div>
                <p>
                Lead designer for rich artifacts feature, enabling users to generate interactive charts, tables, and more using Copilot for real-time collaboration.</p>
              </div>
            </div>
            <img className="img" src={RichArtifacts} alt="Copilot rich artifacts project cover" loading="lazy" />
          </div>
        </AnimatedFeature>

        <AnimatedFeature delay={1}>
          <div id="loop code blocks" className="project" onClick={()=>window.open("/loop-code-blocks","_self")}>
            <div className="item">
              <div>
                <h3 style={{marginBottom: "8px"}}>Loop code blocks</h3>
                <h5>Feb 2023 - May 2023</h5>
              </div>
              <div>
                <p>One of the two designers who developed code blocks, enabling users to share code within their Loop workspaces and pages.</p>
              </div>
            </div>
            <img className="img" src={LoopCodeBlocks} alt="Loop code blocks cover" loading="lazy" />
          </div>
        </AnimatedFeature>
{/* 
          <div id="loop integration in teams" className="project" onClick={()=>window.open("/loop-integration","_self")}>
            <div className="item">
              <div>
                <h3 style={{marginBottom: "8px"}}>Loop integrations in Teams</h3>
                <h5>Feb 2023 - Feb 2024</h5>
              </div>
              <div>
                <p>Led the UX of integrating Microsoft Loop features into Microsoft Teams, making it easier to collaborate on Loop content without having to leave the Teams experience.</p>
              </div>
            </div>
            <img className="img" src={LoopIntegration} alt="Loop Integration in Teams project cover" />
            </div> */}

            <AnimatedFeature delay={2}>
              <div id="loop paywall" className="project" onClick={()=>window.open("/loop-paywall","_self")}>
                <div className="item">
                  <div>
                    <h3 style={{marginBottom: "8px"}}>Loop paywall</h3>
                    <h5>May 2023 - Oct 2023</h5>
                  </div>
                  <div>
                    <p>One of two designers that designed the Loop premium experience and the freemium experience for enterprise and stand alone customers.</p>
                  </div>
                </div>
                <img className="img" src={LoopPaywall} alt="Loop Paywall project cover" loading="lazy" />
              </div>
            </AnimatedFeature>

            <AnimatedFeature delay={3}>
              <div id="morgan stanley" className="project" onClick={()=>window.open("/morgan-stanley","_self")}>
                <div className="item">
                  <div>
                    <h3 style={{marginBottom: "8px"}}>Morgan Stanley</h3>
                    <h5>Dec 2020 - Feb 2022</h5>
                  </div>
                  <div>
                    <p>Lead designer for the new retirement platform by Morgan Stanley. This experience helps clients track retirement goals to help facilitate better conversations with financial advisors.</p>
                  </div>
                </div>
                <img className="img" src={MorganStanley} alt="Morgan Stanley project cover" loading="lazy" />
              </div>
            </AnimatedFeature>

            <AnimatedFeature delay={4}>
              <div id="samana" className="project" onClick={()=>window.open("/samana","_self")}>
                <div className="item">
                  <div>
                    <h3 style={{marginBottom: "8px"}}>Samana</h3>
                    <h5>May 2020 - Jan 2021</h5>
                  </div>
                  <div>
                    <p>Co-founded and crafted the UX for Samana.lk, Sri Lanka's first trilingual nonprofit online catalog of mental health services. This trilingual nonprofit venture strives to improve access to mental health services across the nation.</p>
                  </div>
                </div>
                <img className="img" src={Samana} alt="Samana project cover" loading="lazy" />
              </div>
            </AnimatedFeature>
          </div>
        <AnimatedFeature delay={2.5}>
          <div className="projects-title">
            <h5>Past work</h5><img src={ArrowDown} alt="Arrow down" style={{ width: "24px", marginLeft: "8px"}} />
          </div>
        </AnimatedFeature>

          <div className="project-list">
            <div className="row">
              <AnimatedFeature delay={2.75}>
                <div id="go studio fx" className="project" onClick={()=>window.open("/go-studio-fx","_self")}>
                  <div className="item">
                    <div>
                      <h3 style={{marginBottom: "8px"}}>Go Studio FX</h3>
                      <h5>Jul 2018 - Dec 2019</h5>
                    </div>
                    <div>
                      <p>Lead designer on Go Studio FX, which is an iOS app that enables artists to create music videos using TC Helicon's famous audio effects, and share with their fans.</p>
                    </div>
                  </div>
                  <img className="img" src={GoStudioFX} alt="Go Studio FX project cover" loading="lazy" />
                </div>
              </AnimatedFeature>
              <AnimatedFeature delay={2.8}>
                <div id="blender" className="project" onClick={()=>window.open("/blender","_self")}>
                  <div className="item">
                    <div>
                      <h3 style={{marginBottom: "8px"}}>Blender</h3>
                      <h5>May 2018 - Aug 2018</h5>
                    </div>
                    <div>
                      <p>Lead designer on the companion app for Blender, which is an audio mixer where bands can plug in their instruments and jam quietly with headphones.</p>
                    </div>
                  </div>
                  <img className="img" src={Blender} alt="Blender project cover" loading="lazy" />
                </div>
              </AnimatedFeature>
            </div>

            <div className="row">
              <AnimatedFeature delay={2.9}>
                <div id="goxlr" className="project" onClick={()=>window.open("/go-xlr","_self")}>
                  <div className="item">
                    <div>
                      <h3 style={{marginBottom: "8px"}}>GoXLR</h3>
                      <h5>May 2018 - Oct 2018</h5>
                    </div>
                    <div>
                      <p>Lead designer on the GoXLR companion app on Windows. GoXLR is the first XLR based streaming audio mixer designed specifically for streamers.</p>
                    </div>
                  </div>
                  <img className="img" src={GoXLR} alt="GoXLR project cover" loading="lazy" />
                </div>
              </AnimatedFeature>
              <AnimatedFeature delay={2.925}>
                <div className="project" onClick={()=>window.open("/teamwrk","_self")}>
                  <div className="item">
                    <div>
                      <h3 style={{marginBottom: "8px"}}>Teamwrk</h3>
                      <h5>May 2017 - Sep 2017</h5>
                    </div>
                    <div>
                      <p>Teamwrk is the first ever product I designed professionally. It is a white labbeled CRM platform where clients can manage internal communications and files within thier organization.</p>
                    </div>
                  </div>
                  <img className="img" src={Teamwrk} alt="Teamwrk project cover" loading="lazy" />
                </div>
              </AnimatedFeature>
            </div>
        </div>
      </div>
    </AnimatedPage>
  );
};

export default Work;